package top.wcpe.allincore.ui.runtime.style

import top.wcpe.allincore.ui.runtime.render.model.GradientType
import top.wcpe.allincore.ui.runtime.render.model.MaskType

/**
 * [StyleVisual] → 视觉 modifier 链的转换器（ADR-0041 生产化前置）。
 *
 * 把上帝类 [StyleVisual] 的「元素自配静态视觉效果」翻译为有序 [VisualModifier] 链，供渲染器经
 * [top.wcpe.allincore.ui.runtime.render.ModifierDispatcher] 分派，替代渲染器里硬编码的逐效果 if 链。
 * **纯函数、零平台依赖、可单测**。
 *
 * 范围与边界（[toBoxPaintModifiers]）：只覆盖 `BoxRenderer` 从 [StyleVisual] 静态字段绘制的盒级 paint 效果，
 * **严格沿用 `BoxRenderer` 现序**（阴影 → 表面 → 程序纹理 → 霓虹 → 流光 → 能量）与 `EffectsCompositor` 的
 * 可见性 gating。**不含**主题态（theme glow / scanline）与动态态（RippleAnimator 涟漪）——那些由渲染器在
 * live 路径另行处理；亦不含作用域型效果（blur / blendMode / colorFilter，见 [ScopedVisualModifier]）。
 */
object StyleVisualModifiers {

    private const val ALPHA_SHIFT = 24

    /**
     * 把 [visual] 的作用域 / 包裹型效果按 live 渲染的嵌套序（外→内）转为 [ScopedVisualModifier] 链。
     *
     * 顺序与 `UIRenderer` / `RenderStateStack` 现状一致：ColorFilter（passState 最外层）→ BlendMode → Blur（最内层），
     * 经 [top.wcpe.allincore.ui.runtime.render.ModifierDispatcher.dispatchScoped] 时按此序 begin 外→内、end 内→外。
     * 默认值（无 filter / NORMAL / blur=0）的效果不入链（零回归）。backdrop-filter 是单点 paint（非作用域），不在此。
     */
    fun toScopedModifiers(visual: StyleVisual): List<ScopedVisualModifier> {
        val result = mutableListOf<ScopedVisualModifier>()
        if (visual.hasColorFilter()) {
            result += ScopedVisualModifier.ColorFilter(ColorMatrix.fromStyle(visual))
        }
        if (visual.hasBlendMode()) {
            result += ScopedVisualModifier.BlendMode(visual.blendMode())
        }
        if (visual.hasBlur()) {
            result += ScopedVisualModifier.Blur(visual.filterBlur())
        }
        return result
    }

    /**
     * 把 [visual] 的 backdrop-filter 背景模糊转为单点 paint modifier。
     *
     * backdrop blur 不是 begin/end 作用域，而是在元素自身绘制前采样已落屏背景并回填元素矩形；
     * 未配置时返回空链，保持零开销路径。
     */
    fun toBackdropModifiers(visual: StyleVisual): List<VisualModifier> {
        if (!visual.hasBackdropBlur()) return emptyList()
        return listOf(VisualModifier.BackdropBlur(visual.backdropBlur()))
    }

    /**
     * 把 [visual] 的盒级 paint 效果按 `BoxRenderer` 现序转为 [VisualModifier] 链。[backgroundColor] 为元素背景色
     * （作 Surface 纯色基色 / styled 路径基色）。无可见效果时返回空链。
     *
     * = [toBoxBaseModifiers] + [toBoxEffectModifiers]。`BoxRenderer` 生产化时为保留「表面后、效果前」插入 theme
     * scanline 的现序，应分两段分派（base → scanline → effect），故另拆这两个分段方法。
     */
    fun toBoxPaintModifiers(visual: StyleVisual, backgroundColor: Int): List<VisualModifier> =
        toBoxBaseModifiers(visual, backgroundColor) + toBoxEffectModifiers(visual)

    /** 盒级**底层**效果（阴影 → 表面）：theme scanline 之前绘制的部分。 */
    fun toBoxBaseModifiers(visual: StyleVisual, backgroundColor: Int): List<VisualModifier> {
        val result = mutableListOf<VisualModifier>()
        addShadows(visual, result)
        addSurface(visual, backgroundColor, result)
        return result
    }

    /** 盒级**叠加**效果（程序纹理 → 霓虹 → 流光 → 能量）：theme scanline 之后绘制的部分。 */
    fun toBoxEffectModifiers(visual: StyleVisual): List<VisualModifier> {
        val result = mutableListOf<VisualModifier>()
        addProceduralTexture(visual, result)
        addNeon(visual, result)
        addFlow(visual, result)
        addEnergy(visual, result)
        return result
    }

    /** 阴影：多重阴影优先（逆序遍历 + 可见过滤，与 BoxRenderer.submitBoxShadows 一致），否则回退单阴影字段。
     *  各条阴影携带宿主圆角，使阴影 SDF 与圆角对齐（与 BoxRenderer.submitOneShadow 一致）。 */
    private fun addShadows(visual: StyleVisual, out: MutableList<VisualModifier>) {
        val corner = visual.cornerRadii()
        if (visual.hasBoxShadows()) {
            visual.boxShadows().asReversed()
                .filter { it.isVisible() }
                .forEach { out += VisualModifier.Shadow(it, corner) }
            return
        }
        if (visual.hasShadow()) {
            out += VisualModifier.Shadow(
                ShadowSpec(
                    visual.shadowOffsetX(),
                    visual.shadowOffsetY(),
                    visual.shadowBlur(),
                    visual.shadowSpread(),
                    visual.shadowColor(),
                    visual.shadowInset(),
                ),
                corner,
            )
        }
    }

    /**
     * 表面：有 styled 效果（圆角 / 边框 / 渐变 / 遮罩）或不透明背景色时绘制（与 BoxRenderer 一致）。
     * 渐变类型决定 [Brush]：LINEAR/RADIAL 用渐变画刷，否则纯色背景。
     */
    private fun addSurface(visual: StyleVisual, backgroundColor: Int, out: MutableList<VisualModifier>) {
        val hasStyledSurface = visual.cornerRadiusTL() > 0f ||
            visual.cornerRadiusTR() > 0f ||
            visual.cornerRadiusBR() > 0f ||
            visual.cornerRadiusBL() > 0f ||
            visual.borderWidth() > 0f ||
            visual.gradientType() != GradientType.NONE ||
            visual.maskType() != MaskType.NONE
        val hasOpaqueBg = (backgroundColor ushr ALPHA_SHIFT) != 0
        if (!hasStyledSurface && !hasOpaqueBg) return

        val brush = when (visual.gradientType()) {
            GradientType.LINEAR -> Brush.Linear(visual.gradientStops(), visual.gradientAngleRadians())
            GradientType.RADIAL -> Brush.Radial(
                visual.gradientStops(),
                visual.radialCenterX(),
                visual.radialCenterY(),
                visual.radialRadius(),
            )

            else -> Brush.Solid(backgroundColor)
        }
        val mask = if (visual.maskType() != MaskType.NONE) {
            Mask(visual.maskType(), visual.maskParam1(), visual.maskParam2(), visual.maskParam3())
        } else {
            null
        }
        out += VisualModifier.Surface(brush, visual.cornerRadii(), visual.borderWidth(), visual.borderColor(), mask)
    }

    /** 程序纹理：kind 非空白时绘制，携宿主圆角。 */
    private fun addProceduralTexture(visual: StyleVisual, out: MutableList<VisualModifier>) {
        val kind = visual.proceduralTextureKind()
        if (kind.isNullOrBlank()) return
        out += VisualModifier.ProceduralTexture(
            kind,
            visual.proceduralColorA(),
            visual.proceduralColorB(),
            visual.proceduralScale(),
            visual.proceduralDensity(),
            visual.proceduralIntensity(),
            visual.cornerRadii(),
        )
    }

    /** 霓虹（元素显式）：intensity > 0 且颜色非透明时绘制（与 EffectsCompositor 元素显式分支一致；主题 glow 留 live），携宿主圆角。 */
    private fun addNeon(visual: StyleVisual, out: MutableList<VisualModifier>) {
        if (visual.neonIntensity() <= 0f) return
        if ((visual.neonColor() ushr ALPHA_SHIFT) == 0) return
        out += VisualModifier.Neon(
            visual.neonColor(),
            visual.neonIntensity(),
            visual.neonSize(),
            visual.neonAnimated(),
            visual.cornerRadii(),
        )
    }

    /** 流光：speed > 0、width > 0 且颜色非透明时绘制，携宿主圆角。 */
    private fun addFlow(visual: StyleVisual, out: MutableList<VisualModifier>) {
        if (visual.flowSpeed() <= 0f) return
        if (visual.flowWidth() <= 0f) return
        if ((visual.flowColor() ushr ALPHA_SHIFT) == 0) return
        out += VisualModifier.Flow(
            visual.flowColor(),
            visual.flowSpeed(),
            visual.flowWidth(),
            visual.flowAngleDeg(),
            visual.flowMode(),
            visual.cornerRadii(),
        )
    }

    /** 能量场：intensity > 0 且颜色非透明时绘制，携宿主圆角。 */
    private fun addEnergy(visual: StyleVisual, out: MutableList<VisualModifier>) {
        if (visual.energyIntensity() <= 0f) return
        if ((visual.energyColor() ushr ALPHA_SHIFT) == 0) return
        out += VisualModifier.Energy(
            visual.energyColor(),
            visual.energyIntensity(),
            visual.energyFrequency(),
            visual.energySpeed(),
            visual.energyPattern(),
            visual.cornerRadii(),
        )
    }
}
